import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { PrioridadeTriagem } from '../../dominio/didatico/ocupantes'
import type { CenaTabletop, MaterialAbsorvente, ObjetoTabletop } from '../../dominio/tabletop/tipos'
import {
  VITIMAS_POR_ID,
  ajustarProblemaManualmente,
  alternarCronometro,
  alternarLiberacaoNaTela,
  alternarModoExibicao,
  atualizarTempoSimulado,
  avancarEstadoVitima,
  criarSessaoAtendimento,
  atualizarAvaliacaoCena,
  definirGrauEncarceramento,
  registrarAcaoEquipe,
  definirTrocaLuvasAPH,
  declararRotaExtracao,
  declararTriagem,
  definirFase,
  definirFocoVitima,
  definirNivel,
  definirVitimaIdentificada,
  montarEstadoPublico,
  ajustarRiscoManualmente,
  definirRiscoIdentificado,
  registrarAcaoRisco,
  registrarAnotacaoMestre,
  registrarOperacao,
  registrarMarco,
  registrarIntervencao,
  registrarInterpretacao,
  revelarAcao,
  type EstadoProblemaClinico,
  type AcaoEquipeAtendimento,
  type AvaliacaoCenaTabletop,
  type GrauEncarceramento,
  type FaseAtendimento,
  type FichaVitimaAtendimento,
  type IdAcaoAvaliacao,
  type InterpretacaoLetra,
  type LetraXabcde,
  type MotivoAjusteManual,
  type SituacaoRisco,
  type NivelAtendimento,
  type ProblemaClinicoFicha,
  type RegiaoAnatomicaVitima,
  type SessaoAtendimentoTabletop,
  type AparenciaRisco,
  type TipoAcaoRisco,
  type RegistroOperacao,
  type TipoMarcoOcorrencia,
  type TipoRotaExtracao,
} from '../../dominio/tabletop/atendimento'
import {
  INTERVALO_SINAL_VIDA_MS,
  canalProjecao,
  ehJanelaDeProjecao,
  type RelogioPublicoTabletop,
} from './canalProjecaoTabletop'

export interface RetornoUseAtendimentoTabletop {
  readonly sessao: SessaoAtendimentoTabletop
  /** Derivado de `vitima.fichaAtendimentoId` na cena; vítima fora do mapa = "ficha não configurada". */
  readonly vinculosFichas: Readonly<Record<string, string>>
  readonly vitimaFocoObjeto: ObjetoTabletop | null
  readonly vitimaFocoFicha: FichaVitimaAtendimento | null
  readonly colaresColocados: Readonly<Record<string, boolean>>
  readonly triagensDeclaradas: Readonly<Record<string, PrioridadeTriagem>>
  /** sangramento visível na cena, por vítima, enquanto o problema não é controlado */
  readonly sangramentos: Readonly<Record<string, 'ativo' | 'parcial'>>
  /** aparência dos riscos na sessão, para a cena 3D */
  readonly riscos: Readonly<Record<string, AparenciaRisco>>
  readonly registrarAcaoRiscoCena: (idObjeto: string, tipo: TipoAcaoRisco, material?: MaterialAbsorvente) => void
  readonly definirIdentificacaoRisco: (idObjeto: string, identificado: boolean) => void
  /** situação decidida pelo instrutor; `null` volta ao cálculo pelas ações */
  readonly ajustarRisco: (idObjeto: string, situacao: SituacaoRisco | null, motivo: MotivoAjusteManual) => void
  /** registra uma operação num veículo (corte, vidro, proteção, equipamento) com os alertas do momento */
  readonly registrarOperacaoVeiculo: (dados: Omit<RegistroOperacao, 'id' | 'tempoSimuladoSegundos'>) => void
  readonly atualizarAvaliacaoCena: (alteracoes: Partial<AvaliacaoCenaTabletop>) => void
  readonly definirGrauEncarceramentoVitima: (idVitima: string, grau: GrauEncarceramento) => void
  readonly registrarAcaoEquipeVitima: (idVitima: string, tipo: AcaoEquipeAtendimento, rotulo: string) => void
  readonly confirmarTrocaLuvasAPH: () => void
  readonly revelar: (idVitima: string, idAcao: IdAcaoAvaliacao) => void
  readonly declararTriagemVitima: (idVitima: string, prioridade: PrioridadeTriagem, justificativa?: string) => void
  readonly declararRotaVitima: (idVitima: string, rota: TipoRotaExtracao, justificativa?: string) => void
  readonly aplicarIntervencao: (
    idVitima: string,
    tipo: string,
    rotulo: string,
    parametros: Record<string, string | number | boolean>,
    justificativa?: string,
    regiao?: RegiaoAnatomicaVitima,
  ) => void
  readonly alternarLiberacao: (idVitima: string, idAcao: IdAcaoAvaliacao) => void
  readonly ajustarProblema: (
    idVitima: string,
    problema: ProblemaClinicoFicha,
    novoEstado: EstadoProblemaClinico,
    motivo: MotivoAjusteManual,
  ) => void
  readonly registrarMarcoOcorrencia: (tipo: TipoMarcoOcorrencia) => void
  /** `'inicial'` ou o id de um estágio da ficha (ver `estagiosDaVitima`) */
  readonly evoluirEstado: (idVitima: string, novoEstado: string) => void
  readonly registrarInterpretacaoVitima: (idVitima: string, letra: LetraXabcde, interpretacao: InterpretacaoLetra) => void
  readonly registrarAnotacaoVitima: (idVitima: string, anotacao: string) => void
  readonly definirFaseAtendimento: (fase: FaseAtendimento) => void
  readonly definirNivelAtendimento: (nivel: NivelAtendimento) => void
  readonly alternarPlayPauseCronometro: () => void
  readonly resetarCronometro: () => void
  readonly alternarModoExibicao: (modo?: 'aluno' | 'instrutor') => void
  readonly selecionarVitimaFoco: (idVitima: string | null) => void
  readonly vincularFichaAVitima: (idObjetoVitima: string, idFicha: string | null) => void
  readonly definirIdentificacaoVitima: (idVitima: string, identificada: boolean) => void
  /** Retoma um atendimento salvo (ver `usePersistenciaSessao`). */
  readonly restaurarSessaoAtendimento: (sessao: SessaoAtendimentoTabletop) => void
  /** Começa uma sessão de atendimento nova (cronômetro, achados, histórico), sem tocar no cenário. */
  readonly reiniciarSessaoAtendimento: () => void
  readonly abrirProjecaoSegundaTela: () => void
}

interface OpcoesAtendimentoTabletop {
  /** Grava o vínculo na cena (ver `useTabletop.vincularFicha`) — é de lá que ele volta como `vinculosFichas`. */
  readonly aoVincularFicha: (idObjetoVitima: string, idFicha: string | null) => void
  /**
   * Muda a cada sessão iniciada (ver `useTabletop.serialSessao`): o atendimento
   * (cronômetro, achados, riscos, operações, histórico) recomeça junto.
   */
  readonly serialSessao?: number
}

export function useAtendimentoTabletop(
  cena: CenaTabletop,
  { aoVincularFicha, serialSessao }: OpcoesAtendimentoTabletop,
): RetornoUseAtendimentoTabletop {
  const [sessao, setSessao] = useState<SessaoAtendimentoTabletop>(() => criarSessaoAtendimento())

  // O vínculo vítima → ficha mora na cena (`vitima.fichaAtendimentoId`), escolhido
  // explicitamente pelo autor. Não há mais vínculo automático pelo índice da
  // vítima: sem ficha escolhida, a vítima fica como "ficha não configurada".
  const vinculosFichas = useMemo(() => {
    const vinculos: Record<string, string> = {}
    for (const objeto of cena.objetos) {
      const idFicha = objeto.vitima?.fichaAtendimentoId
      if (idFicha && VITIMAS_POR_ID[idFicha]) vinculos[objeto.id] = idFicha
    }
    return vinculos
  }, [cena.objetos])

  // Cronômetro da ocorrência
  useEffect(() => {
    if (!sessao.cronometroRodando) return
    const intervalo = setInterval(() => {
      setSessao((antiga) => atualizarTempoSimulado(antiga, antiga.tempoSimuladoSegundos + 1))
    }, 1000)
    return () => clearInterval(intervalo)
  }, [sessao.cronometroRodando])

  // --- Projeção (2ª tela) ----------------------------------------------------
  // Só a janela do instrutor transmite. Vai apenas o estado PÚBLICO, montado
  // campo a campo (`montarEstadoPublico`); o relógio segue em mensagem própria.
  const transmite = !ehJanelaDeProjecao()

  const estadoPublico = useMemo(
    () => montarEstadoPublico(cena, sessao, (id) => (vinculosFichas[id] ? VITIMAS_POR_ID[vinculosFichas[id]] ?? null : null)),
    // o tempo muda a cada segundo e não entra no estado público: só os campos que ele lê
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cena,
      vinculosFichas,
      sessao.id,
      sessao.fase,
      sessao.estadosVitimas,
      sessao.vitimasIdentificadas,
      sessao.marcos,
      sessao.riscos,
    ],
  )
  const relogio: RelogioPublicoTabletop = useMemo(
    () => ({ tempoSimuladoSegundos: sessao.tempoSimuladoSegundos, cronometroRodando: sessao.cronometroRodando }),
    [sessao.tempoSimuladoSegundos, sessao.cronometroRodando],
  )

  const revisaoRef = useRef(0)
  const ultimoEstadoRef = useRef(estadoPublico)
  const ultimoRelogioRef = useRef(relogio)
  ultimoEstadoRef.current = estadoPublico
  ultimoRelogioRef.current = relogio

  const enviarEstado = useCallback(() => {
    canalProjecao.enviar({
      tipo: 'ESTADO_PUBLICO',
      revisao: revisaoRef.current,
      estado: ultimoEstadoRef.current,
      relogio: ultimoRelogioRef.current,
    })
  }, [])

  useEffect(() => {
    if (!transmite) return
    revisaoRef.current += 1
    enviarEstado()
  }, [transmite, estadoPublico, enviarEstado])

  useEffect(() => {
    if (!transmite) return
    canalProjecao.enviar({ tipo: 'RELOGIO', idSessao: sessao.id, relogio })
  }, [transmite, relogio, sessao.id])

  // uma projeção aberta depois pede o estado; o sinal de vida permite a ela
  // indicar quando a janela do instrutor foi fechada
  useEffect(() => {
    if (!transmite) return
    const pararDeOuvir = canalProjecao.ouvir((msg) => {
      if (msg.tipo === 'SOLICITAR_ESTADO') enviarEstado()
    })
    const sinalDeVida = setInterval(() => {
      canalProjecao.enviar({ tipo: 'RELOGIO', idSessao: ultimoEstadoRef.current.idSessao, relogio: ultimoRelogioRef.current })
    }, INTERVALO_SINAL_VIDA_MS)
    return () => {
      pararDeOuvir()
      clearInterval(sinalDeVida)
    }
  }, [transmite, enviarEstado])

  const vincularFichaAVitima = useCallback(
    (idObjetoVitima: string, idFicha: string | null) => aoVincularFicha(idObjetoVitima, idFicha),
    [aoVincularFicha],
  )

  const nomeDaVitima = useCallback(
    (idVitima: string) => cena.objetos.find((o) => o.id === idVitima)?.nome,
    [cena.objetos],
  )

  const definirIdentificacaoVitima = useCallback(
    (idVitima: string, identificada: boolean) => {
      const nome = nomeDaVitima(idVitima)
      setSessao((antiga) => definirVitimaIdentificada(antiga, idVitima, identificada, nome))
    },
    [nomeDaVitima],
  )

  /**
   * Registrar uma avaliação, triagem ou intervenção numa vítima implica que a
   * guarnição já chegou até ela — marca como identificada no mesmo passo, para
   * o instrutor não precisar de um clique a mais. Só abrir a ficha não marca.
   */
  const comIdentificacao = useCallback(
    (antiga: SessaoAtendimentoTabletop, idVitima: string) =>
      definirVitimaIdentificada(antiga, idVitima, true, nomeDaVitima(idVitima)),
    [nomeDaVitima],
  )

  const reiniciarSessaoAtendimento = useCallback(() => {
    setSessao(criarSessaoAtendimento())
  }, [])

  const restaurarSessaoAtendimento = useCallback((salva: SessaoAtendimentoTabletop) => {
    setSessao(salva)
  }, [])

  // cada sessão iniciada (entrar em Atender, abrir cenário pronto, reiniciar)
  // começa um atendimento novo — nada passa de uma aula para a outra
  const serialAnterior = useRef(serialSessao)
  useEffect(() => {
    if (serialAnterior.current === serialSessao) return
    serialAnterior.current = serialSessao
    setSessao(criarSessaoAtendimento())
  }, [serialSessao])

  const selecionarVitimaFoco = useCallback((idVitima: string | null) => {
    setSessao((antiga) => definirFocoVitima(antiga, idVitima))
  }, [])

  const vitimaFocoObjeto = useMemo(() => {
    if (!sessao.focoVitimaId) return null
    return cena.objetos.find((o) => o.id === sessao.focoVitimaId) ?? null
  }, [cena.objetos, sessao.focoVitimaId])

  const vitimaFocoFicha = useMemo(() => {
    if (!sessao.focoVitimaId) return null
    const idFicha = vinculosFichas[sessao.focoVitimaId]
    return idFicha ? VITIMAS_POR_ID[idFicha] ?? null : null
  }, [sessao.focoVitimaId, vinculosFichas])

  const revelar = useCallback(
    (idVitima: string, idAcao: IdAcaoAvaliacao) => {
      const idFicha = vinculosFichas[idVitima]
      const ficha = idFicha ? VITIMAS_POR_ID[idFicha] : null
      if (!ficha) return
      setSessao((antiga) => revelarAcao(comIdentificacao(antiga, idVitima), idVitima, idAcao, ficha))
    },
    [vinculosFichas, comIdentificacao],
  )

  const declararTriagemVitima = useCallback(
    (idVitima: string, prioridade: PrioridadeTriagem, justificativa?: string) => {
      setSessao((antiga) => declararTriagem(comIdentificacao(antiga, idVitima), idVitima, prioridade, justificativa))
    },
    [comIdentificacao],
  )

  const declararRotaVitima = useCallback(
    (idVitima: string, rota: TipoRotaExtracao, justificativa?: string) => {
      setSessao((antiga) => declararRotaExtracao(antiga, idVitima, rota, justificativa))
    },
    [],
  )

  const aplicarIntervencao = useCallback(
    (
      idVitima: string,
      tipo: string,
      rotulo: string,
      parametros: Record<string, string | number | boolean>,
      justificativa?: string,
      regiao?: RegiaoAnatomicaVitima,
    ) => {
      const idFicha = vinculosFichas[idVitima]
      const ficha = idFicha ? VITIMAS_POR_ID[idFicha] ?? null : null
      setSessao((antiga) =>
        registrarIntervencao(comIdentificacao(antiga, idVitima), idVitima, tipo, rotulo, parametros, justificativa, {
          ficha,
          regiao,
        }),
      )
    },
    [comIdentificacao, vinculosFichas],
  )

  const alternarLiberacao = useCallback((idVitima: string, idAcao: IdAcaoAvaliacao) => {
    setSessao((antiga) => alternarLiberacaoNaTela(antiga, idVitima, idAcao))
  }, [])

  const ajustarProblema = useCallback(
    (idVitima: string, problema: ProblemaClinicoFicha, novoEstado: EstadoProblemaClinico, motivo: MotivoAjusteManual) => {
      setSessao((antiga) => ajustarProblemaManualmente(antiga, idVitima, problema.id, problema.rotulo, novoEstado, motivo))
    },
    [],
  )

  /**
   * Um toque por marco. Ler o acionamento já dispara o cronômetro; registrar
   * a chegada leva o roteiro para a fase de cena, se ainda estiver no despacho.
   */
  const registrarMarcoOcorrencia = useCallback((tipo: TipoMarcoOcorrencia) => {
    setSessao((antiga) => {
      let nova = registrarMarco(antiga, tipo)
      if (tipo === 'despacho') nova = alternarCronometro(nova, true)
      return nova
    })
  }, [])

  const evoluirEstado = useCallback((idVitima: string, novoEstado: string) => {
    setSessao((antiga) => avancarEstadoVitima(antiga, idVitima, novoEstado))
  }, [])

  const registrarInterpretacaoVitima = useCallback(
    (idVitima: string, letra: LetraXabcde, interpretacao: InterpretacaoLetra) => {
      setSessao((antiga) => registrarInterpretacao(antiga, idVitima, letra, interpretacao))
    },
    [],
  )

  const registrarAnotacaoVitima = useCallback((idVitima: string, anotacao: string) => {
    setSessao((antiga) => registrarAnotacaoMestre(antiga, idVitima, anotacao))
  }, [])

  const definirFaseAtendimento = useCallback((fase: FaseAtendimento) => {
    setSessao((antiga) => definirFase(antiga, fase))
  }, [])

  const definirNivelAtendimento = useCallback((nivel: NivelAtendimento) => {
    setSessao((antiga) => definirNivel(antiga, nivel))
  }, [])

  const alternarPlayPauseCronometro = useCallback(() => {
    setSessao((antiga) => alternarCronometro(antiga))
  }, [])

  const resetarCronometro = useCallback(() => {
    setSessao((antiga) => {
      const pausada = alternarCronometro(antiga, false)
      return atualizarTempoSimulado(pausada, 0)
    })
  }, [])

  const alternarModo = useCallback((modo?: 'aluno' | 'instrutor') => {
    setSessao((antiga) => alternarModoExibicao(antiga, modo))
  }, [])

  const colaresColocados = useMemo(() => {
    const resultado: Record<string, boolean> = {}
    Object.entries(sessao.estadosVitimas).forEach(([idVitima, estado]) => {
      const tem = estado.intervencoesRealizadas.some((i) => i.tipo === 'colar_cervical')
      if (tem) resultado[idVitima] = true
    })
    return resultado
  }, [sessao.estadosVitimas])

  const triagensDeclaradas = useMemo(() => {
    const resultado: Record<string, PrioridadeTriagem> = {}
    Object.entries(sessao.estadosVitimas).forEach(([idVitima, estado]) => {
      if (estado.triagemDeclarada) {
        resultado[idVitima] = estado.triagemDeclarada
      }
    })
    return resultado
  }, [sessao.estadosVitimas])

  const sangramentos = useMemo(() => estadoPublico.sangramentos, [estadoPublico])
  const riscos = useMemo(() => estadoPublico.riscos, [estadoPublico])

  const registrarAcaoRiscoCena = useCallback(
    (idObjeto: string, tipo: TipoAcaoRisco, material?: MaterialAbsorvente) => {
      const objeto = cena.objetos.find((o) => o.id === idObjeto)
      if (!objeto) return
      setSessao((antiga) => registrarAcaoRisco(antiga, objeto, tipo, material))
    },
    [cena.objetos],
  )

  const registrarOperacaoVeiculo = useCallback(
    (dados: Omit<RegistroOperacao, 'id' | 'tempoSimuladoSegundos'>) => {
      const nome = cena.objetos.find((o) => o.id === dados.idVeiculo)?.nome
      setSessao((antiga) => registrarOperacao(antiga, dados, nome))
    },
    [cena.objetos],
  )

  const atualizarLeituraCena = useCallback((alteracoes: Partial<AvaliacaoCenaTabletop>) => {
    setSessao((antiga) => atualizarAvaliacaoCena(antiga, alteracoes))
  }, [])

  const definirEncarceramentoVitima = useCallback((idVitima: string, grau: GrauEncarceramento) => {
    setSessao((antiga) => definirGrauEncarceramento(antiga, idVitima, grau))
  }, [])

  const registrarAcaoEquipeVitima = useCallback((idVitima: string, tipo: AcaoEquipeAtendimento, rotulo: string) => {
    setSessao((antiga) => registrarAcaoEquipe(antiga, idVitima, tipo, rotulo))
  }, [])

  const confirmarTrocaLuvasAPH = useCallback(() => {
    setSessao((antiga) => definirTrocaLuvasAPH(antiga))
  }, [])

  const definirIdentificacaoRisco = useCallback(
    (idObjeto: string, identificado: boolean) => {
      const objeto = cena.objetos.find((o) => o.id === idObjeto)
      if (!objeto) return
      setSessao((antiga) => definirRiscoIdentificado(antiga, objeto, identificado))
    },
    [cena.objetos],
  )

  const ajustarRisco = useCallback(
    (idObjeto: string, situacao: SituacaoRisco | null, motivo: MotivoAjusteManual) => {
      const objeto = cena.objetos.find((o) => o.id === idObjeto)
      if (!objeto) return
      setSessao((antiga) => ajustarRiscoManualmente(antiga, objeto, situacao, motivo))
    },
    [cena.objetos],
  )

  const abrirProjecaoSegundaTela = useCallback(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    url.searchParams.set('projecao', 'tabletop')
    window.open(url.toString(), '_blank', 'noopener,noreferrer,width=1280,height=720')
  }, [])

  return {
    sessao,
    vinculosFichas,
    vitimaFocoObjeto,
    vitimaFocoFicha,
    colaresColocados,
    triagensDeclaradas,
    sangramentos,
    riscos,
    registrarAcaoRiscoCena,
    definirIdentificacaoRisco,
    ajustarRisco,
    registrarOperacaoVeiculo,
    atualizarAvaliacaoCena: atualizarLeituraCena,
    definirGrauEncarceramentoVitima: definirEncarceramentoVitima,
    registrarAcaoEquipeVitima,
    confirmarTrocaLuvasAPH,
    revelar,
    declararTriagemVitima,
    declararRotaVitima,
    aplicarIntervencao,
    alternarLiberacao,
    ajustarProblema,
    registrarMarcoOcorrencia,
    evoluirEstado,
    registrarInterpretacaoVitima,
    registrarAnotacaoVitima,
    definirFaseAtendimento,
    definirNivelAtendimento,
    alternarPlayPauseCronometro,
    resetarCronometro,
    alternarModoExibicao: alternarModo,
    selecionarVitimaFoco,
    vincularFichaAVitima,
    definirIdentificacaoVitima,
    reiniciarSessaoAtendimento,
    restaurarSessaoAtendimento,
    abrirProjecaoSegundaTela,
  }
}
